class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.onHistoryShow = (event) => {
      const navigation = performance.getEntriesByType('navigation')[0];
      if (event.persisted || (navigation && navigation.type === 'back_forward')) {
        this.needsHistoryRefresh = true;
        this.refreshAfterHistory();
      }
    };
    this.onHistoryHide = () => {
      this.openRequest = (this.openRequest || 0) + 1;
      this.needsHistoryRefresh = true;
      if (this.historyController) this.historyController.abort();
      this.historyController = null;
      this.historyRefresh = null;
      this.inert = false;
      this.removeAttribute('aria-busy');
    };
  }

  connectedCallback() {
    window.addEventListener('pageshow', this.onHistoryShow);
    window.addEventListener('pagehide', this.onHistoryHide);
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.onHistoryShow);
    window.removeEventListener('pagehide', this.onHistoryHide);
    this.onHistoryHide();
  }

  refreshAfterHistory() {
    if (this.historyRefresh) return this.historyRefresh;
    const controller = new AbortController();
    this.historyController = controller;
    const previousInner = this.querySelector('.drawer__inner');
    // Prevent quantity/checkout actions against the cached cart during refresh.
    this.inert = true;
    this.setAttribute('aria-busy', 'true');
    const timeout = setTimeout(() => controller.abort(), 8000);
    const url = new URL(routes.cart_url, window.location.origin);
    url.searchParams.set('section_id', 'cart-drawer');
    this.historyRefresh = fetch(url, { cache: 'no-store', signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Cart refresh failed');
        return response.text();
      })
      .then((html) => {
        if (controller !== this.historyController) return false;
        // An add-to-cart may have already installed a newer section while this
        // request was in flight. Never overwrite that successful cart update.
        if (this.querySelector('.drawer__inner') !== previousInner) {
          this.needsHistoryRefresh = false;
          return true;
        }
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fresh = parsed.querySelector('#CartDrawer');
        const host = parsed.querySelector('cart-drawer');
        const current = this.querySelector('#CartDrawer');
        if (!fresh || !host || !current) throw new Error('Missing cart section');
        // Count line quantities from the SAME server snapshot as the drawer,
        // avoiding a second request that could disagree with these contents.
        const count = Array.from(fresh.querySelectorAll('input[name="updates[]"]'))
          .reduce((total, input) => total + (Number(input.value) || 0), 0);
        current.innerHTML = fresh.innerHTML;
        this.classList.toggle('is-empty', host.classList.contains('is-empty'));
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        document.querySelectorAll('[data-af-cart-count]').forEach((badge) => {
          badge.textContent = count;
          badge.hidden = count === 0;
        });
        this.needsHistoryRefresh = false;
        if (this.classList.contains('active')) {
          this.inert = false;
          trapFocus(this.querySelector('.drawer__inner'), this.querySelector('.drawer__close'));
        }
        return true;
      })
      .catch(() => {
        if (controller === this.historyController && this.querySelector('.drawer__inner') !== previousInner) {
          this.needsHistoryRefresh = false;
          return true;
        }
        // A visible restored drawer must not offer checkout with stale items.
        // The normal cart page remains the fallback if section loading fails.
        if (controller === this.historyController && this.classList.contains('active')) {
          window.location.assign(routes.cart_url);
        }
        return false;
      })
      .finally(() => {
        clearTimeout(timeout);
        if (controller === this.historyController) {
          this.historyController = null;
          this.historyRefresh = null;
          this.inert = false;
          this.removeAttribute('aria-busy');
        }
      });
    return this.historyRefresh;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.needsHistoryRefresh) {
      const request = this.openRequest = (this.openRequest || 0) + 1;
      return this.refreshAfterHistory().then((refreshed) => {
        if (request !== this.openRequest || !this.isConnected) return;
        if (refreshed) this.open(triggeredBy);
        else window.location.assign(routes.cart_url);
      });
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.openRequest = (this.openRequest || 0) + 1;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // The empty vs populated layout is keyed off `is-empty` on the <cart-drawer>
    // host (component-cart-drawer.css: hides the header, turns .drawer__inner into
    // a centered 1-row grid). renderContents only runs after a successful add, so
    // the cart is non-empty — clear it from the HOST (the old code cleared it from
    // .drawer__inner, which never carries it, so an add to an empty cart left the
    // host flagged is-empty and the populated drawer rendered blank).
    this.classList.remove('is-empty');
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      // getSectionInnerHTML now returns null instead of throwing when a section is absent;
      // assigning that directly would write the literal string "null" into the drawer.
      const html = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (html !== null) sectionElement.innerHTML = html;
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    // Null-safe for the same reason as cart.js: a section missing from the response, or a
    // selector that does not match, must not throw — the caller skips the assignment.
    if (!html) return null;
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  }

  getSectionsToRender() {
    // This theme's custom header has no #cart-icon-bubble element (it uses a
    // [data-af-cart-count] badge, synced separately via the cart-update event).
    // Requesting that section makes Dawn's re-render call null.querySelector and
    // throw — surfacing as "There was an error while updating your cart." Render
    // only the drawer.
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    // Only the drawer — no #cart-icon-bubble in this theme (see CartDrawer above).
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
